import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect, useContext } from 'react';
import DetailsSidebar from '../../component/detailssidebar/DetailsSidebar';
import InputText from '../../component/input/InputText';
import InputAmount from '../../component/input/InputAmount';
import Dropdown from '../../component/input/Dropdown';
import { MessageContext } from '../../context/MessageContext';
import { FormattedUtils } from '../../utils/FormattedUtils';
import { DeviceService } from '../../service/DeviceService';
import { CashLevelService } from '../../service/CashLevelService';
import { useAuth } from '../../hook/useAuth';
import './CashReconciliationSidebar.scss';
import useValidator from '../../utils/validator/useValidator';

const amountsDiffer = (a, b) => Math.abs(Number(a) - Number(b)) > 0.005;

const CashReconciliationSidebar = ({ visible, onHide, onSuccess, viewMode, reconciliationData, onDiscrepancyMismatch, onMatchConfirm }) => {
    const { t } = useTranslation();
    const { getUserName } = useAuth();
    const currentUserName = getUserName() || '–';
    const isMounted = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const [mainKey, setMainKey] = useState(0);
    const [deviceOptions, setDeviceOptions] = useState([]);
    const validator = useValidator();
    const [form, setForm] = useState({
        bagId: '',
        deviceId: null,
        specialistId: null,
        amountMoney: null,
        actualMoney: null
    });
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext);

    const fetchDeviceOptions = async () => {
        try {
            const devices = await DeviceService.getDevicesForCashOperations();
            if (isMounted.current) {
                setDeviceOptions((devices || []).map(d => ({ id: d.id, label: d.name })));
            }
        } catch (err) {
            if (isMounted.current) showErrorMessage(t('cashoperations.cashOperationSidebar.error.fetchDeviceOptions'));
        }
    };

    useEffect(() => {
        isMounted.current = true;
        if (visible) {
            if (viewMode === 'EXISTING' && reconciliationData) {
                setForm({
                    bagId: reconciliationData.bagId || '',
                    deviceId: null,
                    specialistId: null,
                    amountMoney: reconciliationData.enteredAmount ?? null,
                    actualMoney: reconciliationData.actualAmount ?? null
                });
            } else {
                const data = reconciliationData || {};
                const profile = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('profile') || 'null') : null;
                const specialistId = profile?.id ? { id: profile.id, label: profile.name || getUserName() || '–' } : null;
                setForm({
                    bagId: data.bagId,
                    deviceId: null,
                    specialistId,
                    amountMoney: data.enteredAmount ?? null,
                    actualMoney: null
                });
            }
            if (viewMode === 'NEW') {
                fetchDeviceOptions();
            }
            validator.hideMessages();
        }
        return () => {
            isMounted.current = false;
        };
    }, [visible, viewMode, reconciliationData]);

    const getTitle = () => {
        return t('cashoperations.reconciliationSidebar.title');
    };

    const handleOnHide = () => {
        validator.hideMessages();
        if (onHide) onHide();
    };

    const handleSave = () => {
        if (viewMode === 'EXISTING' && reconciliationData) {
            if (!validator.allValid()) {
                validator.showMessages();
                setMainKey((k) => k + 1);
                return;
            }
            const operationId = reconciliationData.operationId;
            if (!operationId) {
                showErrorMessage(t('cashoperations.reconciliationSidebar.error.update'));
                return;
            }
            const entered = reconciliationData.enteredAmount;
            if (amountsDiffer(entered, form.actualMoney) && onDiscrepancyMismatch) {
                onDiscrepancyMismatch({
                    mode: 'EXISTING',
                    enteredAmount: entered,
                    actualAmount: form.actualMoney,
                    operationId,
                    currency: reconciliationData.currency
                });
                return;
            }
            if (onMatchConfirm) {
                onMatchConfirm({
                    mode: 'EXISTING',
                    operationId,
                    actualAmount: form.actualMoney,
                    bagId: reconciliationData.bagId
                });
                return;
            }
            setIsSaving(true);
            CashLevelService.updateReconciliation(operationId, { actualAmount: form.actualMoney })
                .then(() => {
                    if (isMounted.current) {
                        showSuccessMessage(t('cashoperations.reconciliationSidebar.success.updated'));
                        if (onSuccess) onSuccess();
                        if (onHide) onHide();
                    }
                })
                .catch((err) => {
                    console.error('Reconciliation update error:', err);
                    if (isMounted.current) {
                        showErrorMessage(t('cashoperations.reconciliationSidebar.error.update'));
                    }
                })
                .finally(() => {
                    if (isMounted.current) setIsSaving(false);
                });
            return;
        }

        // NEW mode – create reconciliation
        if (!validator.allValid()) {
            validator.showMessages();
            setMainKey((k) => k + 1);
            return;
        }
        if (!form.specialistId?.id) {
            showErrorMessage(t('cashoperations.reconciliationSidebar.error.specialistRequired'));
            return;
        }

        const request = {
            deviceId: form.deviceId,
            specialistId: form.specialistId.id,
            bagId: form.bagId.trim(),
            actualAmount: form.actualMoney,
            enteredAmount: form.amountMoney != null ? form.amountMoney : undefined
        };

        if (amountsDiffer(form.amountMoney, form.actualMoney) && onDiscrepancyMismatch) {
            onDiscrepancyMismatch({
                mode: 'NEW',
                enteredAmount: form.amountMoney,
                actualAmount: form.actualMoney,
                createRequest: request
            });
            return;
        }

        if (onMatchConfirm) {
            onMatchConfirm({
                mode: 'NEW',
                createRequest: request,
                bagId: form.bagId.trim()
            });
            return;
        }

        setIsSaving(true);
        CashLevelService.createReconciliation(request)
            .then(() => {
                if (isMounted.current) {
                    showSuccessMessage(t('cashoperations.reconciliationSidebar.success.created'));
                    if (onSuccess) onSuccess();
                    if (onHide) onHide();
                }
            })
            .catch((err) => {
                console.error('Reconciliation create error:', err);
                if (isMounted.current) {
                    showErrorMessage(t('cashoperations.reconciliationSidebar.error.create'));
                }
            })
            .finally(() => {
                if (isMounted.current) setIsSaving(false);
            });
    };

    const isExistingMode = viewMode === 'EXISTING';
    const displayData = reconciliationData || {};

    return (
        <DetailsSidebar
            className="cash-reconciliation-sidebar"
            visible={visible}
            onHide={handleOnHide}
            onSave={handleSave}
            contentKey={mainKey}
            isSaving={isSaving}
            viewMode={viewMode}
            title={getTitle()}
        >
            <div className="reconciliation-form">
                {isExistingMode ? (
                    <div className="reconciliation-field reconciliation-field-readonly">
                        <span className="reconciliation-label">{t('cashoperations.reconciliationSidebar.bagBarcode')}</span>
                        <span className="reconciliation-value reconciliation-value-barcode">
                            {displayData.bagId || displayData.barcode || '–'}
                        </span>
                    </div>
                ) : (
                    <div className="reconciliation-field">
                        <InputText
                            id="bagId"
                            label={t('cashoperations.reconciliationSidebar.bagBarcode')}
                            value={form.bagId}
                            onChange={(e) => setForm((prev) => ({ ...prev, bagId: e.target.value }))}
                            validator={validator}
                            validations="required"
                        />
                    </div>
                )}

                <div className="reconciliation-field">
                    {isExistingMode ? (
                        <InputText
                            id="device"
                            label={t('cashoperations.reconciliationSidebar.device')}
                            value={displayData.deviceName || ''}
                            disabled
                        />
                    ) : (
                        <Dropdown
                            id="deviceId"
                            label={t('cashoperations.reconciliationSidebar.device')}
                            options={deviceOptions}
                            value={form.deviceId}
                            onChange={(e) => setForm((prev) => ({ ...prev, deviceId: e.value }))}
                            optionLabel="label"
                            optionValue="id"
                            placeholder={t('common.select')}
                            validator={validator}
                            validations="required"
                        />
                    )}
                </div>

                <div className="reconciliation-field">
                    <InputText
                        id="specialist"
                        label={t('cashoperations.reconciliationSidebar.specialist')}
                        value={currentUserName}
                        disabled
                    />
                </div>

                <div className="reconciliation-field">
                    {isExistingMode ? (
                        <InputText
                            id="amountMoney"
                            label={t('cashoperations.reconciliationSidebar.amountMoney')}
                            value={
                                displayData.enteredAmount !== null && displayData.enteredAmount !== undefined
                                    ? FormattedUtils.formatPrice(displayData.enteredAmount)
                                    : ''
                            }
                            disabled
                        />
                    ) : (
                        <InputAmount
                            id="amountMoney"
                            label={t('cashoperations.reconciliationSidebar.amountMoney')}
                            value={form.amountMoney}
                            onChange={(e) => setForm(prev => ({ ...prev, amountMoney: e.target?.value ?? e.value }))}
                            validator={validator}
                            validations="required"
                        />
                    )}
                </div>

                <div className="reconciliation-field">
                    <InputAmount
                        id="actualMoney"
                        label={t('cashoperations.reconciliationSidebar.actualMoney')}
                        value={form.actualMoney}
                        onChange={(e) => setForm((prev) => ({ ...prev, actualMoney: e.target?.value ?? e.value }))}
                        validator={validator}
                        validations=""
                    />
                </div>
            </div>
        </DetailsSidebar>
    );
};

export default CashReconciliationSidebar;
